import { useCallback, useSyncExternalStore } from 'react';
import { io } from 'socket.io-client';
import { whatsappApi } from '../services/api';
import { getSocketBaseUrl } from '../utils/runtimeUrls';

// ========== Singleton Socket.IO + stato globale ==========
const SOCKET_URL = getSocketBaseUrl();

// Stato globale condiviso (fuori dal componente React)
let _state = {
  connectionStatus: 'disconnected',
  qrCode: null,
  connectedNumber: null,
  lines: [],
  lineQr: {},
  unreadCount: 0,
  lastMessage: null,
  error: null,
};
let _socket = null;
let _subscribers = new Set();
let _messageListeners = new Set();
let _initialFetchDone = false;

function _notify() {
  _subscribers.forEach(fn => fn());
}

function _setState(patch) {
  _state = { ..._state, ...patch };
  _notify();
}

function _deriveConnection(lines = _state.lines) {
  const readyLine = lines.find(line => line.status === 'ready');
  const activeLine = readyLine || lines[0] || null;
  return {
    connectionStatus: readyLine ? 'ready' : (activeLine?.status || _state.connectionStatus || 'disconnected'),
    connectedNumber: readyLine?.connected_phone || activeLine?.connected_phone || null
  };
}

function _mergeLine(lineId, patch) {
  if (!lineId) {
    const legacyPatch = { ...patch };
    if (patch.status) legacyPatch.connectionStatus = patch.status;
    if (patch.connected_phone || patch.number) legacyPatch.connectedNumber = patch.connected_phone || patch.number;
    _setState(legacyPatch);
    return;
  }

  const id = Number(lineId);
  let found = false;
  const nextLines = _state.lines.map((line) => {
    if (Number(line.id) !== id) return line;
    found = true;
    return { ...line, ...patch, id };
  });
  if (!found) nextLines.push({ id, label: `Linea ${id}`, ...patch });
  _setState({ lines: nextLines, ..._deriveConnection(nextLines) });
}

async function _refreshLines() {
  const res = await whatsappApi.getLines();
  const lines = res.data?.data || [];
  const lineQr = Object.fromEntries(Object.entries(_state.lineQr).filter(([id]) =>
    lines.some(line => Number(line.id) === Number(id) && line.status === 'qr_pending')
  ));
  _setState({ lines, lineQr, qrCode: Object.values(lineQr)[0] || null, ..._deriveConnection(lines) });
  return lines;
}

function _ensureSocket() {
  if (_socket) return;

  const socket = io(`${SOCKET_URL}/whatsapp`, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 3000,
    reconnectionAttempts: Infinity,
  });

  _socket = socket;

  socket.on('connect', () => {
    console.log('📱 [WA-Socket] Connesso');
    _refreshLines().catch(() => {});
  });

  socket.on('status', (data) => {
    const lineId = data.line_id || data.whatsapp_line_id || null;
    const patch = { status: data.status };
    if (data.number) patch.connected_phone = data.number;
    patch.error = data.error || null;
    patch.last_error = data.error || null;
    if (data.status !== 'qr_pending') {
      patch.qrAvailable = false;
      const lineQr = { ..._state.lineQr };
      if (lineId) delete lineQr[lineId];
      _setState({ lineQr, qrCode: Object.values(lineQr)[0] || null });
    }
    _mergeLine(lineId, patch);
  });

  socket.on('qr', (data) => {
    const lineId = data.line_id || data.whatsapp_line_id || null;
    if (lineId) {
      _setState({ lineQr: { ..._state.lineQr, [lineId]: data.qr }, qrCode: data.qr });
      _mergeLine(lineId, { status: 'qr_pending', qrAvailable: true });
    } else {
      _setState({ qrCode: data.qr, connectionStatus: 'qr_pending' });
    }
  });

  socket.on('message', (data) => {
    const patch = { lastMessage: data };
    if (data.direction === 'inbound') {
      patch.unreadCount = _state.unreadCount + 1;
    }
    _setState(patch);
    _messageListeners.forEach(fn => fn(data));
  });

  socket.on('message_ack', (data) => {
    _messageListeners.forEach(fn => fn({ type: 'ack', ...data }));
  });

  socket.on('sync_complete', (data) => {
    console.log(`📱 [WA-Socket] Sync completata: ${data.imported} messaggi importati`);
    _messageListeners.forEach(fn => fn({ type: 'sync_complete', ...data }));
  });

  socket.on('line_deleted', (data) => {
    const lineId = Number(data.line_id || data.whatsapp_line_id);
    if (!lineId) return;
    const nextLines = _state.lines.filter(line => Number(line.id) !== lineId);
    const lineQr = { ..._state.lineQr };
    delete lineQr[lineId];
    _setState({ lines: nextLines, lineQr, ..._deriveConnection(nextLines) });
  });

  socket.on('disconnect', () => {
    console.log('📱 [WA-Socket] Disconnesso');
  });

  // Fetch stato iniziale (una sola volta)
  if (!_initialFetchDone) {
    _initialFetchDone = true;
    _refreshLines().catch(() => {});
    whatsappApi.getStatus()
      .then(res => {
        const { status, number } = res.data?.data || {};
        const patch = {};
        if (status) patch.connectionStatus = status;
        if (number) patch.connectedNumber = number;
        if (Object.keys(patch).length) _setState(patch);
      })
      .catch(() => {});
    whatsappApi.getUnreadCount()
      .then(res => {
        _setState({ unreadCount: res.data?.data?.count || 0 });
      })
      .catch(() => {});
  }
}

// snapshot immutabile per useSyncExternalStore
function _getSnapshot() {
  return _state;
}

function _subscribe(cb) {
  _ensureSocket();
  _subscribers.add(cb);
  return () => {
    _subscribers.delete(cb);
  };
}

// ========== Hook pubblico ==========
export default function useWhatsApp() {
  const state = useSyncExternalStore(_subscribe, _getSnapshot);

  const onMessage = useCallback((fn) => {
    _messageListeners.add(fn);
    return () => { _messageListeners.delete(fn); };
  }, []);

  const resetUnread = useCallback(() => {
    _setState({ unreadCount: 0 });
  }, []);

  return {
    refreshLines: _refreshLines,
    connectionStatus: state.connectionStatus,
    qrCode: state.qrCode,
    connectedNumber: state.connectedNumber,
    lines: state.lines,
    lineQr: state.lineQr,
    unreadCount: state.unreadCount,
    lastMessage: state.lastMessage,
    error: state.error,
    onMessage,
    resetUnread,
  };
}
